import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd } from '@angular/router';
import { of, from, EMPTY, Observable } from 'rxjs';
import { map, catchError, switchMap, tap, take, mergeMap, exhaustMap, concatMap, withLatestFrom, filter, finalize } from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import * as DataActions from '../data/data.actions';
import { AuthService } from '../../services/auth/auth.service';
import { TokenStateService } from '../../services/token-state/token-state.service';
import { TokenSyncService } from '../../services/token-sync/token-sync.service';
import { LoadingService } from '../../services/loading/loading.service';
import { PushNotificationService } from '../../services/push-notification/push-notification.service';
import { selectCurrentPortal, selectIsAuthenticated, selectIsAuthInitialized } from './auth.selectors';
import { ComponentStateService } from '../../services/component-state/component-state.service';
import { SiteCacheService } from '../../services/site-cache/site-cache.service';
import { UserService } from '../../services/user/user.service';
import { clearAuthLocalStorage, markSkipSilentRefreshAfterLogout } from '../../services/auth/auth-storage.constants';
import { NativeAuthStorageService } from '../../services/auth/native-auth-storage.service';
import { SplashScreenService } from '../../services/splash-screen/splash-screen.service';
import {
  consumeOAuthContinueRedirect,
  getRouterPath,
  isLoginRoute,
} from '../../services/auth/oauth-continue.util';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private authService: AuthService,
    private router: Router,
    private tokenStateService: TokenStateService,
    private tokenSyncService: TokenSyncService,
    private loadingService: LoadingService,
    private pushNotificationService: PushNotificationService,
    private componentStateService: ComponentStateService,
    private siteCacheService: SiteCacheService,
    private userService: UserService,
    private nativeAuthStorage: NativeAuthStorageService,
    private splashScreenService: SplashScreenService
  ) {}

  // Add new effect to handle root path navigation
  handleRootNavigation$ = createEffect(() => 
    this.store.select(selectIsAuthInitialized).pipe(
      filter(isInitialized => isInitialized),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectCurrentPortal),
        this.router.events.pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          map(event => event.url)
        )
      ),
      filter(([isInitialized, isAuthenticated, currentPortal, currentUrl]) => 
        isAuthenticated && currentUrl === '/'
      ),
      tap(([isInitialized, isAuthenticated, currentPortal]) => {
        const landingPage = currentPortal === 'administration' ? '/dashboard' :
                           currentPortal === 'user' ? '/profile' :
                           currentPortal === 'external' ? '/profile' : '/dashboard';
        
        this.router.navigate([landingPage]);
      })
    ),
    { dispatch: false }
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ username, password, authMethodId, redirectUrl }) =>
        this.authService.login(username, password, authMethodId).pipe(
          switchMap(async ({ token, user }) => {
            this.loadingService.hide();
            
            // Store the token first
            await this.tokenStateService.setToken(token);
            
            // Broadcast the token update
            this.tokenSyncService.broadcastMessage('TOKEN_UPDATED', { token });
            
            return AuthActions.loginSuccess({ 
              token,
              user, 
              isAuthenticated: true,
              shouldNavigate: true
            });
          }),
          catchError(error => {
            console.error('Login failure effect triggered:', error);
            this.loadingService.hide();
            const errorMessage = error.status === 401 ? 'Invalid username or password' : 'An error occurred during login';
            return of(AuthActions.loginFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      withLatestFrom(this.store.select(selectCurrentPortal)),
      tap(([action, currentPortal]) => {
      }),
      switchMap(([action, currentPortal]) => {
        const currentPath = getRouterPath(this.router.url);
        const isRootOrLogin = currentPath === '/' || currentPath === '/login';

        if (action.shouldNavigate && consumeOAuthContinueRedirect()) {
          return EMPTY;
        }

        const redirectUrl = sessionStorage.getItem('redirectUrl');
        
        if (redirectUrl && action.shouldNavigate) {
          // Clear the redirect URL and navigate to it
          sessionStorage.removeItem('redirectUrl');
          return from(this.router.navigateByUrl(redirectUrl)).pipe(
            tap(() => {
            })
          );
        }
        
        // Only navigate to landing page if we're on root/login page AND shouldNavigate is true
        if (isRootOrLogin && action.shouldNavigate) {
          return this.getLandingPageForPortal(currentPortal).pipe(
            tap(landingPage => {
              this.router.navigate([landingPage]);
            })
          );
        }
        return EMPTY;
      })
    ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(() => {
        this.authService.prepareForLogout();
        this.tokenSyncService.beginExplicitLogout();

        return from(this.authService.logout()).pipe(
          map(() => AuthActions.logoutSuccess({ shouldNavigate: false })),
          catchError((error) => {
            console.error('[AuthEffects] Logout error:', error);
            return of(AuthActions.logoutSuccess({ shouldNavigate: false }));
          }),
          finalize(() => {
            void this.finalizeLogoutLocally();
          })
        );
      })
    )
  );
  
  // Add a separate effect to handle state clearing after logout success
  // This is mostly a no-op now since we use window.location.href for navigation
  logoutSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.logoutSuccess),
  ), { dispatch: false });

  // Handle logout failure
  logoutFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutFailure),
      // Ensure we only handle one failure action
      exhaustMap((action) => {
        console.error('[AuthEffects] Logout failed:', action.error);
        return from(this.router.navigate(['/login']));
      })
    ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      switchMap(() =>
        from(this.authService.refreshToken()).pipe(
          map(accessToken => AuthActions.refreshTokenSuccess({ accessToken })),
          catchError(error => {
            console.error('[AuthEffects] Token refresh failed:', error);
            return of(AuthActions.refreshTokenFailure({ error }));
          }),
          finalize(() => {
            this.tokenSyncService.releaseRefreshLock();
          })
        )
      )
    )
  );

  refreshTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenSuccess),
      switchMap(({ accessToken }) => 
        from(this.tokenStateService.setToken(accessToken)).pipe(
          map(() => {
            const user = this.authService.extractUserFromToken(accessToken);
            const isOnLoginPage = isLoginRoute(this.router.url);

            // If we're on the login page, this is likely an SSO flow
            // Trigger full login success with navigation
            if (isOnLoginPage && user) {
              if (consumeOAuthContinueRedirect()) {
                return AuthActions.refreshTokenComplete({
                  token: accessToken,
                  user,
                  isAuthenticated: true,
                });
              }
              return AuthActions.loginSuccess({ 
                token: accessToken,
                user,
                isAuthenticated: true,
                shouldNavigate: true
              });
            }
            
            // Otherwise, just complete the refresh without navigation
            return AuthActions.refreshTokenComplete({ 
              token: accessToken,
              user,
              isAuthenticated: true
            });
          }),
          catchError(error => {
            console.error('Error persisting refreshed token:', error);
            return of(AuthActions.refreshTokenFailure({ error }));
          })
        )
      )
    )
  );

  initializeAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initializeAuth),
      switchMap(async () => {
        try {
          const token = await this.tokenStateService.getToken();
          
          if (!token) {
            return AuthActions.initializeAuthSuccess({ token: null, user: null });
          }

          // Validate token
          const user = this.authService.extractUserFromToken(token);
          
          if (!user) {
            await this.tokenStateService.clearToken();
            return AuthActions.initializeAuthSuccess({ token: null, user: null });
          }

          // Start refresh timer for restored token
          this.authService.startRefreshTokenTimer(token);

          return AuthActions.loginSuccess({ 
            token, 
            user,
            isAuthenticated: true,
            shouldNavigate: window.location.pathname === '/' // Only navigate if we're actually on the root path
          });
        } catch (error) {
          console.error('[AuthEffects] Error during auth initialization:', error);
          await this.tokenStateService.clearToken();
          return AuthActions.initializeAuthFailure({ error });
        }
      })
    )
  );

  addFcmToken$ = createEffect(() => this.actions$.pipe(
    ofType(AuthActions.addFcmToken),
    mergeMap((action: { token: string }) =>
      this.authService.deviceIdentifier$.pipe(
        take(1),
        mergeMap((deviceIdentifier: string) =>
          this.pushNotificationService.registerFcmToken(action.token, deviceIdentifier).pipe(
            map(() => AuthActions.addFcmTokenSuccess()),
            catchError(error => {
              console.error('[AuthEffects] Error registering FCM token:', error);
              return of(AuthActions.addFcmTokenFailure({ error }));
            })
          )
        )
      )
    )
  ));

  refreshTokenComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenComplete),
      tap(({ user }) => {
        // No need to update user info here as it's already handled in the auth service
      })
    ),
    { dispatch: false }
  );

  private async finalizeLogoutLocally(): Promise<void> {
    try {
      this.componentStateService.clearAllStates();

      await Promise.race([
        this.tokenStateService.clearToken({ force: true }),
        new Promise<void>((resolve) => setTimeout(resolve, 5000)),
      ]);
      await Promise.race([
        this.nativeAuthStorage.clearRefreshToken(),
        new Promise<void>((resolve) => setTimeout(resolve, 5000)),
      ]);

      clearAuthLocalStorage();
      markSkipSilentRefreshAfterLogout();
      sessionStorage.clear();
    } catch (error) {
      console.error('[AuthEffects] Error during local logout cleanup:', error);
    } finally {
      this.loadingService.hide();
      this.splashScreenService.hide();
      window.location.replace('/login');
    }
  }

  // Add this method to the AuthEffects class
  private getLandingPageForPortal(portal: string | null): Observable<string> {
    if (portal === 'user') {
      return of('/profile');
    }
    
    // For non-user portals, return the landing page immediately
    const landingPage = portal === 'administration' ? '/dashboard' :
                       portal === 'external' ? '/profile' : '/';
    return of(landingPage);
  }
}
